"use client";

import { motion, AnimatePresence } from "framer-motion";
import Link from "next/link";
import Head from "next/head";
import toast from "react-hot-toast";
import { useCart } from "@/contexts/CartContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import PageTransition from "@/components/PageTransition";
import AppImage from "@/components/ui/AppImage";
import Icon from "@/components/ui/AppIcon";
import { HIGH_INCOME_LOCATIONS } from "@/utils/seo";

export default function CartPage() {
  const { cart, cartCount, cartTotal, updateQuantity, removeFromCart, clearCart } = useCart();

  const handleRemoveItem = (productId: string, productTitle: string) => {
    removeFromCart(productId);
    toast.success(
      <div className="flex items-center gap-2">
        <span className="text-lg">🗑️</span>
        <div>
          <p className="font-bold text-sm">Removed from cart</p>
          <p className="text-xs text-plum/60">{productTitle}</p>
        </div>
      </div>,
      { duration: 2000, icon: null }
    );
  };

  const handleClearCart = () => {
    if (cart.length === 0) return;
    clearCart();
    toast.success("Cart cleared!", { duration: 2000 });
  };

  const handleQuantityChange = (productId: string, newQuantity: number) => {
    if (newQuantity < 1) return;
    updateQuantity(productId, newQuantity);
  };

  return (
    <>
      <Head>
        <title>Shopping Cart | My Happy Place Digital - Trusted by Mothers Worldwide</title>
        <meta name="description" content={`Review your cart and checkout premium children's books. Trusted by mothers in ${HIGH_INCOME_LOCATIONS.slice(0, 15).join(", ")}, and 285+ high-income areas. Free shipping over $35.`} />
        <meta name="robots" content="noindex, follow" />
        <link rel="canonical" href="https://myhappypla4132.builtwithrocket.new/cart" />
      </Head>
      <Header />
      <PageTransition>
        <main className="min-h-screen bg-gradient-to-br from-plum-50 via-lavender-50 to-white pt-28 pb-16">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            {/* Header */}
            <div className="text-center mb-12">
              <h1 className="font-fraunces text-4xl md:text-5xl font-bold text-plum mb-3">
                Your Shopping Cart 🛒
              </h1>
              <p className="text-plum/60 text-lg">
                {cartCount === 0 ? "Your cart is empty" : `${cartCount} ${cartCount === 1 ? "item" : "items"} in your cart`}
              </p>
            </div>

            {cart.length === 0 ? (
              <div className="text-center py-16">
                <div className="text-8xl mb-6">📚</div>
                <h2 className="font-fraunces text-2xl font-bold text-plum mb-4">
                  Your cart is empty
                </h2>
                <p className="text-plum/60 mb-8">Add some magical books to get started!</p>
                <Link
                  href="/products"
                  className="btn-primary inline-flex items-center gap-2 px-8 py-4 text-base min-h-[48px]"
                >
                  <Icon name="ShoppingBagIcon" size={20} variant="solid" />
                  Browse Products
                </Link>
              </div>
            ) : (
              <div className="grid lg:grid-cols-3 gap-6 lg:gap-8">
                {/* Cart Items */}
                <div className="lg:col-span-2 space-y-4">
                  <AnimatePresence mode="popLayout">
                    {cart.map((item) => (
                      <motion.div
                        key={item.id}
                        layout
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, x: -100 }}
                        transition={{ duration: 0.3 }}
                        className="bg-white rounded-2xl shadow-dreamy p-4 sm:p-6 flex gap-4 sm:gap-6"
                      >
                        {/* Product Image */}
                        <div className="relative w-24 h-24 sm:w-28 sm:h-28 md:w-32 md:h-32 rounded-xl overflow-hidden bg-plum-50 flex-shrink-0">
                          <AppImage
                            src={item.image}
                            alt={item.alt}
                            fill
                            className="object-cover"
                          />
                        </div>

                        {/* Product Info */}
                        <div className="flex-1 min-w-0">
                          <h3 className="font-fraunces font-bold text-plum text-base sm:text-lg mb-1 line-clamp-2">
                            {item.title}
                          </h3>
                          <p className="text-xs sm:text-sm text-plum/60 mb-3 line-clamp-1">
                            {item.subtitle}
                          </p>

                          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
                            {/* Quantity Controls */}
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => handleQuantityChange(item.id, item.quantity - 1)}
                                disabled={item.quantity <= 1}
                                className="w-10 h-10 rounded-full bg-plum-light/10 hover:bg-plum-light/20 flex items-center justify-center transition-all active:scale-90 disabled:opacity-40 disabled:cursor-not-allowed"
                                aria-label="Decrease quantity"
                              >
                                <Icon name="MinusIcon" size={16} variant="solid" className="text-plum" />
                              </button>
                              <span className="font-bold text-plum w-10 text-center text-base">{item.quantity}</span>
                              <button
                                onClick={() => handleQuantityChange(item.id, item.quantity + 1)}
                                className="w-10 h-10 rounded-full bg-plum-light/10 hover:bg-plum-light/20 flex items-center justify-center transition-all active:scale-90"
                                aria-label="Increase quantity"
                              >
                                <Icon name="PlusIcon" size={16} variant="solid" className="text-plum" />
                              </button>
                            </div>

                            {/* Price & Remove */}
                            <div className="flex items-center gap-3">
                              <span className="font-black text-plum text-lg sm:text-xl">
                                ${(item.price * item.quantity).toFixed(2)}
                              </span>
                              <button
                                onClick={() => handleRemoveItem(item.id, item.title)}
                                className="p-2.5 rounded-full hover:bg-rose/10 text-rose transition-all active:scale-90 min-h-[44px] min-w-[44px] flex items-center justify-center"
                                aria-label={`Remove ${item.title} from cart`}
                              >
                                <Icon name="TrashIcon" size={20} variant="outline" />
                              </button>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </AnimatePresence>

                  {/* Clear Cart Button */}
                  <button
                    onClick={handleClearCart}
                    className="w-full py-4 rounded-xl border-2 border-rose/20 text-rose font-bold hover:bg-rose/5 transition-all active:scale-98 min-h-[48px]"
                  >
                    Clear Cart
                  </button>
                </div>

                {/* Order Summary */}
                <div className="lg:col-span-1">
                  <div className="bg-white rounded-2xl shadow-dreamy p-6 sticky top-28">
                    <h2 className="font-fraunces text-2xl font-bold text-plum mb-6">
                      Order Summary
                    </h2>

                    <div className="space-y-3 mb-6">
                      <div className="flex justify-between text-plum/70">
                        <span>Subtotal ({cartCount} items)</span>
                        <span className="font-bold">${cartTotal.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-plum/70">
                        <span>Shipping</span>
                        <span className="font-bold text-green-600">
                          {cartTotal >= 35 ? "FREE" : "$4.99"}
                        </span>
                      </div>
                      {cartTotal < 35 && (
                        <p className="text-xs text-plum/50 bg-plum-50 rounded-lg p-2">
                          🎉 Add ${(35 - cartTotal).toFixed(2)} more for free shipping!
                        </p>
                      )}
                      <div className="border-t border-lavender/30 pt-3 flex justify-between text-plum text-lg">
                        <span className="font-bold">Total</span>
                        <span className="font-black">
                          ${(cartTotal + (cartTotal >= 35 ? 0 : 4.99)).toFixed(2)}
                        </span>
                      </div>
                    </div>

                    <Link
                      href="/checkout"
                      className="w-full btn-primary py-4 text-base font-bold flex items-center justify-center gap-2 mb-3 min-h-[48px]"
                    >
                      <Icon name="ShoppingBagIcon" size={20} variant="solid" />
                      Proceed to Checkout
                    </Link>

                    <Link
                      href="/products"
                      className="block text-center text-sm text-plum/70 hover:text-plum font-semibold transition-colors min-h-[44px] flex items-center justify-center"
                    >
                      ← Continue Shopping
                    </Link>
                  </div>
                </div>
              </div>
            )}
          </div>
        </main>
      </PageTransition>
      <Footer />
    </>
  );
}